<template>
    <BaseDialog
        v-model:open="isOpen"
        title="Filter people"
        show-close
        dialog-content-class="flex w-full flex-col overflow-hidden md:!w-[36rem] md:!max-w-[36rem] max-h-[90dvh]! md:max-h-[90dvh]!">
        <template #trigger>
            <BaseButton
                theme="neutral"
                size="sm"
                class="gap-2 rounded-full"
                :class="activeCount > 0 ? 'border-valence-grey-750 bg-valence-grey-750 text-ink-inverse hover:bg-valence-grey-850' : 'text-ink hover:border-valence-grey-500'">
                <i class="bi bi-funnel" aria-hidden="true"></i>
                Filter
            </BaseButton>
        </template>
        <div class="flex min-h-0 flex-1 flex-col">
            <div class="flex min-h-0 flex-1 flex-col px-5 pb-5">
                <div class="shrink-0 pb-4">
                    <p class="body-small text-ink-muted">Narrow your people list by selecting one value per facet.</p>
                    <div v-if="activeCount > 0" class="body-small-semibold mt-3 inline-flex items-center gap-2 rounded-full bg-chip-neutral-bg px-3 py-1 text-chip-neutral-text">
                        <i class="bi bi-sliders2 text-sm" aria-hidden="true"></i>
                        {{ activeCount }} active {{ activeCount === 1 ? "filter" : "filters" }}
                    </div>
                </div>
                <div class="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6">
                    <div v-for="facet in facets" :key="facet.id" class="rounded-xl border border-card-border p-4 space-y-2">
                        <div class="flex items-center justify-between gap-3">
                            <div class="body-small-semibold text-ink">{{ facet.name }}</div>
                            <span v-if="localFilters[facet.id]" class="body-small text-ink-muted">{{ localFilters[facet.id] }}</span>
                        </div>
                        <BaseSelect
                            :model-value="localFilters[facet.id] || ALL_FILTER_OPTION"
                            :options="facetOptions(facet)"
                            trigger-class="w-full!"
                            @update:model-value="(val: string) => updateFilter(facet.id, val)" />
                    </div>
                </div>
            </div>
            <div class="flex shrink-0 items-center justify-between gap-3 border-t border-valence-grey-200 bg-dialog-surface px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <BaseButton v-if="hasPendingSelections" theme="tertiary" size="sm" class="px-0 text-ink-muted hover:text-button-tertiary-hover-text" @click="clearAll">
                    Clear all
                </BaseButton>
                <span v-else class="body-small text-ink-muted">No filters selected</span>
                <BaseButton theme="primary" size="sm" @click="applyFilters">Apply filters</BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import { computed, ref, watch } from "vue";

interface Facet {
    id: string;
    name: string;
    values: string[];
}

interface Props {
    facets: Facet[];
}

const ALL_FILTER_OPTION = "__all__";

defineProps<Props>();
const model = defineModel<Record<string, string>>({ default: () => ({}) });

const isOpen = ref(false);
const localFilters = ref<Record<string, string>>({ ...model.value });

watch(model, (val) => {
    localFilters.value = { ...val };
});

watch(isOpen, (open) => {
    if (open) {
        localFilters.value = { ...model.value };
    }
});

const activeCount = computed(() => Object.values(model.value).filter((v) => v).length);
const hasPendingSelections = computed(() => Object.values(localFilters.value).some((v) => v));

const facetOptions = (facet: Facet) => [{ key: ALL_FILTER_OPTION, label: "All" }, ...facet.values.map((v) => ({ key: v, label: v }))];

const updateFilter = (facetId: string, value: string) => {
    if (value && value !== ALL_FILTER_OPTION) {
        localFilters.value[facetId] = value;
        return;
    }

    delete localFilters.value[facetId];
    localFilters.value = { ...localFilters.value };
};

const clearAll = () => {
    localFilters.value = {};
};

const applyFilters = () => {
    model.value = { ...localFilters.value };
    isOpen.value = false;
};
</script>
